/**
 * Frontend mirror of backend/sport_categories.py — single source of truth
 * for sport identifiers, labels, emojis, and display ordering.
 *
 * MUST stay in sync with backend enum. When adding a new sport, edit both
 * files in the same commit.
 */

export const SPORTS = {
  ROLLER_SKATE: "roller_skate",
  INLINE_SKATE: "inline_skate",
  SKATEBOARD: "skateboard",
  LONGBOARD: "longboard",
  BMX: "bmx",
  SCOOTER: "scooter",
  SNOW_SPORTS: "snow_sports",
  SHARED: "shared", // Hire Hub only
};

// Ordered list for user-facing pickers. Founding community (roller skate) stays first.
export const USER_FACING_SPORTS = [
  SPORTS.ROLLER_SKATE,
  SPORTS.INLINE_SKATE,
  SPORTS.SKATEBOARD,
  SPORTS.LONGBOARD,
  SPORTS.BMX,
  SPORTS.SCOOTER,
  SPORTS.SNOW_SPORTS,
];

// Display metadata. `color` hints guide filter-pill tinting per tribe.
//
// All emojis are currently blank — a custom icon set is being designed
// externally and will be added later. Consumers must use the pattern
// `{meta.emoji && <span>{meta.emoji}</span>}` so an empty value
// collapses cleanly with no stray whitespace or tofu boxes. Pills/badges
// fall back to colour + text-only, which still gives plenty of visual
// distinction per tribe.
export const SPORT_META = {
  [SPORTS.ROLLER_SKATE]: {
    label: "Rollerskating",
    short: "Rollerskating",
    emoji: "",
    color: "#EC4899", // pink — matches existing roller skate brand accent
  },
  [SPORTS.INLINE_SKATE]: {
    label: "Inline Skates",
    short: "Inline Skates",
    emoji: "",
    color: "#8B5CF6", // violet
  },
  [SPORTS.SKATEBOARD]: {
    label: "Skateboard",
    short: "Skateboard",
    emoji: "",
    color: "#F59E0B", // amber
  },
  [SPORTS.LONGBOARD]: {
    label: "Longboard",
    short: "Longboard",
    emoji: "",
    color: "#10B981", // emerald
  },
  [SPORTS.BMX]: {
    label: "BMX",
    short: "BMX",
    emoji: "",
    color: "#EF4444", // red
  },
  [SPORTS.SCOOTER]: {
    label: "Scooter",
    short: "Scooter",
    emoji: "",
    color: "#06B6D4", // cyan
  },
  [SPORTS.SNOW_SPORTS]: {
    label: "Snow Sports",
    short: "Snow Sports",
    emoji: "",
    color: "#3B82F6", // blue
  },
  [SPORTS.SHARED]: {
    label: "Shared / Event Gear",
    short: "Shared",
    emoji: "",
    color: "#64748B", // slate — neutral
  },
};

// Legacy → new category remapping (pre-migration events).
export const LEGACY_CATEGORY_MAP = {
  skate: SPORTS.ROLLER_SKATE,
  snow_sports: SPORTS.SNOW_SPORTS,
};

export const DEFAULT_SPORT = SPORTS.ROLLER_SKATE;

/** Normalise any legacy/invalid category string into a valid sport. */
export function normaliseSport(value, fallback = DEFAULT_SPORT) {
  if (!value) return fallback;
  if (LEGACY_CATEGORY_MAP[value]) return LEGACY_CATEGORY_MAP[value];
  if (Object.values(SPORTS).includes(value)) return value;
  return fallback;
}

/** Safe lookup — returns fallback meta rather than undefined for unknown values. */
export function getSportMeta(sport) {
  return SPORT_META[sport] || SPORT_META[DEFAULT_SPORT];
}

/** Human-readable label with emoji — use everywhere for consistency.
    Falls back to label-only when the sport has no emoji (skating-family
    on older Android — see note on SPORT_META). */
export function sportLabel(sport, { withEmoji = true, short = false } = {}) {
  const meta = getSportMeta(sport);
  const label = short ? meta.short : meta.label;
  return withEmoji && meta.emoji ? `${meta.emoji} ${label}` : label;
}
